import type { Goal } from '@medplum/fhirtypes';
import React from 'react';

import { Grid, Icon, Stack, Tag, Text } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';
import {
  type GroupedObservations,
  TrendSparkline,
  formatMeasureValue,
} from '@eluve/fhir-observations';
import {
  GoalAchievementTag,
  formatFhirDate,
  goalProgress,
  resourceDisplayName,
} from '@eluve/fhir-renderer';

import {
  type GoalList,
  formatGoalTarget,
  goalToken,
  latestTrackedValue,
  trackedGoalCount,
  trackedObservationGroup,
  trackingLabel,
} from '../goal-list';
import { isPendingReview } from '../review-markers';

/**
 * The Overview-tab Goals card (ENG-5367): pending AI drafts and in-progress
 * goals first (primary leading), achieved / ended goals below. Rows are
 * composed from renderer primitives (`resourceDisplayName`,
 * `GoalAchievementTag`, `formatFhirDate`); the tracked-observation treatment
 * (sparkline + now/target line) and Draft badging are app chrome, per the
 * renderer's presentational contract.
 */
export type GoalsCardProps = {
  list: GoalList;
  /** The loaded observation window goals resolve tracked series from. */
  grouped: GroupedObservations | null;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Open the detail drawer for a goal (routing owned by the section). */
  onOpenGoal: (token: string) => void;
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-12">
    <Text size="sm" color="tertiary" weight="medium">
      {children}
    </Text>
  </Grid>
);

const GoalRows: React.FC<{
  goals: Goal[];
  grouped: GroupedObservations | null;
  onOpenGoal: (token: string) => void;
}> = ({ goals, grouped, onOpenGoal }) => (
  <>
    {goals.map((goal) => {
      const pending = isPendingReview(goal);
      const date = formatFhirDate(goal.startDate ?? goal.statusDate);
      const group = trackedObservationGroup(goal, grouped);
      const label = trackingLabel(goal, group);
      const now = latestTrackedValue(group);
      const target = formatGoalTarget(goal);
      const latestPending = group?.pending[group.pending.length - 1];
      const atRisk = goalProgress(goal)?.color === 'warning';
      const secondary = [
        date
          ? goal.startDate
            ? `Since: ${date}`
            : `Updated: ${date}`
          : undefined,
        label ? `Tracking: ${label}` : undefined,
      ]
        .filter(Boolean)
        .join(' · ');
      return (
        <button
          key={goalToken(goal)}
          type="button"
          onClick={() => onOpenGoal(goalToken(goal))}
          data-testid="goal-row"
          className="relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary hover:bg-bg-secondary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0"
        >
          <div className="min-w-0 flex-1">
            <Stack
              direction="horizontal"
              gap={2}
              align="center"
              fullWidth={false}
              className="min-h-6"
            >
              <Text inline size="sm" weight="medium" className="truncate">
                {resourceDisplayName(goal)}
              </Text>
              <GoalAchievementTag goal={goal} />
              {pending && (
                <Tag pill subtle size="xs" color="insights" label="Draft" />
              )}
            </Stack>
            {secondary && (
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="mt-0.5"
              >
                {secondary}
              </Text>
            )}
          </div>
          {(now ?? target) && (
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Text
                inline
                size="sm"
                color="tertiary"
                weight="medium"
                className="tabular-nums"
              >
                {now && (
                  <>
                    Now:{' '}
                    <Text
                      inline
                      size="sm"
                      weight="semibold"
                      className={atRisk ? 'text-text-warning' : undefined}
                    >
                      {formatMeasureValue(now.value, group?.unit)}
                    </Text>
                  </>
                )}
                {now && target && ' · '}
                {target && `Target: ${target}`}
              </Text>
              {group && group.series.length + group.pending.length > 1 && (
                <TrendSparkline
                  className={
                    atRisk ? 'text-text-warning' : 'text-text-positive'
                  }
                  points={group.series.map((point) => ({
                    value: point.value,
                    amended: point.amended,
                  }))}
                  proposedValue={
                    latestPending?.valueQuantity?.value ?? undefined
                  }
                />
              )}
            </div>
          )}
          <Icon
            name="ChevronRight"
            size="xs"
            className="shrink-0 text-text-tertiary"
          />
        </button>
      );
    })}
  </>
);

export const GoalsCard: React.FC<GoalsCardProps> = ({
  list,
  grouped,
  loading,
  failed,
  paging,
  onOpenGoal,
}) => {
  const total = trackedGoalCount(list);
  const isEmpty = total === 0;

  return (
    <section
      data-testid="goals-card"
      className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Goals
        </Text>
      </Stack>

      {failed ? (
        <CenteredNote>Couldn't load goals.</CenteredNote>
      ) : loading ? (
        <CenteredNote>Loading goals…</CenteredNote>
      ) : isEmpty ? (
        <CenteredNote>No goals recorded.</CenteredNote>
      ) : (
        <>
          <div className="p-1">
            <GoalRows
              goals={list.pending}
              grouped={grouped}
              onOpenGoal={onOpenGoal}
            />
            <GoalRows
              goals={list.inProgress}
              grouped={grouped}
              onOpenGoal={onOpenGoal}
            />
            <GoalRows
              goals={list.ended}
              grouped={grouped}
              onOpenGoal={onOpenGoal}
            />
          </div>
          <LoadMoreRow paging={paging} noun="goals" />
        </>
      )}
    </section>
  );
};
